import { QuestionOption } from "../types/landingPage";
import { withScope, captureException } from "@sentry/react";

/**
 * Data structure for cross-referral context
 */
export interface CrossReferralData {
  fromLandingPageId: string;
  answers: Record<string, string | string[]>;
  questionId: string;
  answerValue: string;
}

/**
 * Parsed cross-referral data from URL parameters
 */
export interface ParsedCrossReferralData {
  fromLandingPageId: string | null;
  answers: Record<string, string | string[]>;
  hasData: boolean;
}

/**
 * Configuration for cross-referral redirect
 */
export interface CrossReferralRedirectConfig {
  targetLandingPageId: string;
  sourceAnswers: Record<string, string | string[]>;
  triggerQuestionId: string;
  triggerAnswerValue: string;
  sourceLandingPageId: string;
}

/**
 * Build URL with cross-referral parameters
 */
export function buildCrossReferralUrl(config: CrossReferralRedirectConfig): string {
  const params = new URLSearchParams({
    from: config.sourceLandingPageId,
    [`prefill_${config.triggerQuestionId}`]: config.triggerAnswerValue,
    prefillData: JSON.stringify(config.sourceAnswers),
  });
  
  return `/${config.targetLandingPageId}?${params}`;
}

/**
 * Parse cross-referral data from URL search parameters
 */
export function parseCrossReferralData(searchParams: URLSearchParams): ParsedCrossReferralData {
  const fromLandingPageId = searchParams.get("from");
  const prefillDataStr = searchParams.get("prefillData");
  
  if (!fromLandingPageId || !prefillDataStr) {
    return {
      fromLandingPageId: null,
      answers: {},
      hasData: false,
    };
  }
  
  try {
    const answers = JSON.parse(prefillDataStr) as Record<string, string | string[]>;
    return {
      fromLandingPageId,
      answers,
      hasData: true,
    };
  } catch (error) {
    withScope((scope) => {
      scope.setExtra('component', 'crossReferral');
      scope.setExtra('action', 'parseCrossReferralData');
      scope.setExtra('fromLandingPageId', fromLandingPageId);
      scope.setExtra('prefillDataLength', prefillDataStr?.length || 0);
      captureException(error);
    });
    console.error("Error parsing cross-referral data:", error);
    return {
      fromLandingPageId,
      answers: {},
      hasData: false,
    };
  }
}

/**
 * Check if a selected option should trigger a cross-referral redirect
 */
export function shouldRedirectToCrossReferral(
  selectedOption: QuestionOption | undefined,
  isDisqualifying: boolean
): boolean {
  return !!(
    isDisqualifying && 
    selectedOption?.redirectToLandingPageId && 
    selectedOption.redirectToLandingPageId.trim().length > 0
  );
}

/**
 * Determine if a question should be auto-advanced based on prefilled data
 */
export function shouldAutoAdvanceQuestion(
  questionId: string,
  prefillAnswers: Record<string, string | string[]>,
  questionIndex: number = 0
): boolean {
  // Auto-advance the diabetes question if it's the first question and we have prefilled data for it
  return questionIndex === 0 && questionId === "diabetesType" && !!prefillAnswers.diabetesType;
}

/**
 * Get the next question index when auto-advancing
 */
export function getAutoAdvanceQuestionIndex(
  currentIndex: number,
  totalQuestions: number
): number {
  return Math.min(currentIndex + 1, totalQuestions - 1);
} 